import { useRef, useState, useEffect } from 'react';

/**
 * @typedef {import('react').MutableRefObject<any>} ActiveSpotNumber — the current active Spot number
 * @typedef {boolean} ActiveSpotNumberSetter — documentation for setIsLoading
 * @typedef {[ActiveSpotNumber, ActiveSpotNumberSetter]} ActiveSpot
 */

/**
 * useHover function
 * @return {ActiveSpot}
 */
export function useHover() {
  let [isHover, setHover] = useHoverState(false);

  /**
   * @type {import('react').MutableRefObject<any>} ref
   */
  let ref = useRef(null);

  useEffect(() => {
    let node = ref.current;

    let handleMouseOver = () => setHover(true);

    let handleMouseOut = () => {
      setHover(false);
    };

    if (node) {
      node.addEventListener('mouseover', handleMouseOver);
      node.addEventListener('mouseout', handleMouseOut);

      return () => {
        node.removeEventListener('mouseover', handleMouseOver);
        node.removeEventListener('mouseout', handleMouseOut);
      };
    }
  }, [ref, setHover]);

  return [ref, isHover];
}

/**
 * @typedef {boolean} IsHover — documentation for isLoading
 * @typedef {import('react').Dispatch<import('react').SetStateAction<boolean>>} SetHoverAction — set state of IsHover
 * @typedef {[IsHover, SetHoverAction]} Hover
 */

/**
 * @param {boolean} initState - initial state
 * @return {Hover} Hover
 */
function useHoverState(initState) {
  /**
   * @type {Hover} Hover
   */
  let [isHover, setHover] = useState(initState);
  return [isHover, setHover];
}
